import type { SubsidyRule, SubsidyRuleKind } from '@cpce/ticketing-api';
import type { ColumnDef } from '@tanstack/react-table';
import { CircleQuestionMark } from 'lucide-react';
import { Badge, BadgeColor } from '@/components/Badge';
import { Tooltip } from '@/components/Tooltip';
import type { BadgeOption } from '@/components/table/table.types';
import { formatDate } from '@/utils/date.utils';

const TYPE_TOOLTIP =
  "Les règles « Catégorie » s'appliquent aux prestations qui ciblent la catégorie visée ou l'une de ses sous-catégories, les règles « Produit » aux prestations qui ciblent ce produit. Les limites de quantité et les plafonds sont appliqués par la billetterie et affichés ici pour information.";

const TYPE_OPTIONS: BadgeOption<SubsidyRuleKind>[] = [
  { value: 'category', label: 'Catégorie', color: BadgeColor.BLUE },
  { value: 'product', label: 'Produit', color: BadgeColor.PURPLE },
  { value: 'quantityLimit', label: 'Limite de quantité', color: BadgeColor.ORANGE },
  { value: 'ceiling', label: 'Plafond', color: BadgeColor.GREEN },
];

const ENABLED_OPTIONS: BadgeOption<boolean>[] = [
  { value: true, label: 'Active', color: BadgeColor.GREEN },
  { value: false, label: 'Désactivée', color: BadgeColor.GRAY },
];

const formatPeriod = (rule: SubsidyRule): string => {
  if (!rule.dateDebut && !rule.dateFin) return '';
  const format = (value: string | null) => (value ? formatDate(new Date(value)) : '…');
  return `${format(rule.dateDebut)} → ${format(rule.dateFin)}`;
};

export const buildSubsidyRuleColumns = (
  defaultKinds: SubsidyRuleKind[] | undefined
): ColumnDef<SubsidyRule>[] => [
  {
    id: 'type',
    accessorKey: 'kind',
    header: () => (
      <>
        Type
        <Tooltip text={TYPE_TOOLTIP}>
          <CircleQuestionMark className="size-4 shrink-0 text-gray-400" />
        </Tooltip>
      </>
    ),
    cell: (info) => {
      const option = TYPE_OPTIONS.find((o) => o.value === info.getValue<SubsidyRuleKind>());
      return <Badge color={option?.color}>{option?.label ?? info.getValue<string>()}</Badge>;
    },
    meta: { filterType: 'badge-enum', options: TYPE_OPTIONS, defaultFilter: defaultKinds },
  },
  {
    accessorKey: 'target',
    header: 'Cible',
    meta: { filterType: 'text' },
  },
  {
    accessorKey: 'formula',
    header: 'Subvention',
    meta: { filterType: 'text' },
  },
  {
    accessorKey: 'quantity',
    header: 'Quantité max.',
    cell: (info) => info.getValue<number | null>() ?? '-',
    meta: { filterType: 'numeric' },
  },
  {
    id: 'period',
    accessorFn: formatPeriod,
    header: 'Période',
    cell: (info) => info.getValue<string>() || '-',
    meta: { filterType: 'text' },
  },
  {
    accessorKey: 'enabled',
    header: 'Statut',
    cell: (info) => {
      const option = ENABLED_OPTIONS.find((o) => o.value === info.getValue<boolean>());
      return <Badge color={option?.color}>{option?.label}</Badge>;
    },
    meta: { filterType: 'badge-enum', options: ENABLED_OPTIONS },
  },
];
